import React, { useState, useEffect } from "react";
import { Table, Button, Input, Form, message, Modal, Spin, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;

const SizeManager = () => {
  const [sizes, setSizes] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentEditSize, setCurrentEditSize] = useState(null);

  useEffect(() => {
    const fetchSizes = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/product/sizes");
        if (response.data.success) {
          const formattedSizes = response.data.sizes.map((sz) => ({
            key: sz.id.toString(),
            id: sz.id,
            name: sz.size,
          }));
          setSizes(formattedSizes);
        } else {
          message.error("Failed to fetch sizes.");
        }
      } catch (error) {
        message.error("An error occurred while fetching sizes.");
      } finally {
        setLoading(false);
      }
    };

    fetchSizes();
  }, []);

  const handleAddSize = async (values) => {
    try {
      const response = await axiosInstance.post("/admin/size", {
        size: values.size,
      });

      if (response.status === 201 && response.data.success) {
        const newSize = {
          key: response.data.size.id.toString(),
          id: response.data.size.id,
          name: response.data.size.size,
        };

        setSizes((prevSizes) => [...prevSizes, newSize]);
        message.success("Size added successfully!");
        setAddModalVisible(false);
        form.resetFields();
      } else {
        message.error("Failed to add size.");
      }
    } catch (error) {
      message.error("An error occurred while adding the size.");
    }
  };

  const handleEditSize = async (values) => {
    try {
      const response = await axiosInstance.put(`/admin/size/${currentEditSize.id}`, {
        size: values.size,
      });

      if (response.data.success) {
        setSizes((prevSizes) =>
          prevSizes.map((s) =>
            s.id === currentEditSize.id ? { ...s, name: values.size } : s
          )
        );
        message.success("Size updated successfully!");
        setEditModalVisible(false);
        setCurrentEditSize(null);
      } else {
        message.error("Failed to update size.");
      }
    } catch (error) {
      message.error("An error occurred while updating the size.");
    }
  };

  const handleDeleteSize = (key) => {
    confirm({
      title: "Are you sure you want to delete this size?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await axiosInstance.delete(`/admin/size/${key}`);
          if (response.data.success) {
            setSizes((prev) => prev.filter((s) => s.key !== key));
            message.success("Size deleted successfully!");
          } else {
            // Show backend-provided message if present
            message.error(response.data.message || "Failed to delete size.");
          }
        } catch (error) {
          message.error(
            error?.response?.data?.message ||
              "An error occurred while deleting the size."
          );
        }
      },
    });
  };

  const openEditModal = (record) => {
    setCurrentEditSize(record);
    editForm.setFieldsValue({
      size: record.name,
    });
    setEditModalVisible(true);
  };

  const filteredSizes = sizes.filter((size) =>
    size.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Sr.No",
      key: "srno",
      width: 70,
      render: (text, record, index) => (
        <div className="text-center font-medium text-gray-600">
          {index + 1}
        </div>
      ),
    },
    {
      title: "Size Name",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <div className="text-sm font-medium text-gray-900">
          {name}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Tooltip title="Edit Size">
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              size="small"
              className="bg-blue-500 hover:bg-blue-600 border-blue-500"
            />
          </Tooltip>
          <Tooltip title="Delete Size">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteSize(record.key)}
              size="small"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Sizes</h1>
                <p className="text-gray-600">Organize and manage product dimensions</p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search Input */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Sizes
                </label>
                <Input
                  placeholder="Search size name..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              {/* Stats Card */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Size Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {filteredSizes.length} sizes
                  </div>
                </div>
              </div>

              {/* Add Button */}
              <div className="flex flex-col justify-end">
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setAddModalVisible(true)}
                  className="h-10 bg-blue-600 hover:bg-blue-700 border-blue-600"
                >
                  Add New Size
                </Button>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={filteredSizes}
              columns={columns}
              rowKey="key"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} items`,
                className: "px-6 py-4",
                pageSizeOptions: ['10', '20', '50']
              }}
              scroll={{ x: 'max-content' }}
              className="modern-orders-table"
              rowClassName={(record) => "hover:bg-gray-50 transition-colors duration-200"}
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">No sizes found</div>
                    <div className="text-sm text-gray-400">Try adding a new size or adjusting your search</div>
                  </div>
                )
              }}
            />
          </div>
        </div>
      </div>

      {/* Add Size Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <PlusOutlined className="text-blue-500" />
            <span>Add New Size</span>
          </div>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        className="modern-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleAddSize}>
          <Form.Item
            label={
              <span className="text-sm font-medium text-gray-700">
                Size Name
              </span>
            }
            name="size"
            rules={[{ required: true, message: "Please enter size name!" }]}
          >
            <Input 
              placeholder="Enter size name" 
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>
          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button 
                onClick={() => {
                  setAddModalVisible(false);
                  form.resetFields();
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Add Size
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Size Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <EditOutlined className="text-blue-500" />
            <span>Edit Size</span>
          </div>
        }
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setCurrentEditSize(null);
          editForm.resetFields();
        }}
        footer={null}
        className="modern-modal"
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditSize}>
          <Form.Item
            label={
              <span className="text-sm font-medium text-gray-700">
                Size Name
              </span>
            }
            name="size"
            rules={[{ required: true, message: "Please enter size name!" }]}
          >
            <Input 
              placeholder="Enter size name" 
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>
          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button 
                onClick={() => {
                  setEditModalVisible(false);
                  setCurrentEditSize(null);
                  editForm.resetFields();
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Update Size
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        
        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        
        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        
        .ant-input {
          border-radius: 8px !important;
        }
      `}</style>
    </Spin>
  );
};

export default SizeManager;
